/**
 * `R4-UIM1` — la pantalla «Métricas» de R4.
 *
 * Dos pestañas de estado local, dos tablas, el diccionario, la nota del brazo y
 * tres popups:
 *
 *   1. **Pestaña «Resultados»** · la Tabla 1 con encabezado de DOS niveles
 *      —«Mensajes inductores», «Personas», «Intensidad»—, una fila por periodo en
 *      el orden que fija `posición` (esta pantalla NO reordena), su paginado de
 *      diez, «Qué significa cada métrica» con sus diecisiete entradas y, en el
 *      brazo con derivación, la nota del brazo.
 *   2. **Pestaña «Errores de Meta»** · la Tabla 2 con la LISTA FIJA de seis
 *      columnas —cinco códigos y «Otros errores»—, su paginado y su nota.
 *   3. **Los tres popups** · cargar/editar un periodo y borrarlo, con
 *      `role="dialog"` + `aria-modal`.
 *   4. **Las dos descargas** · un botón por pestaña, con «Descargando…» y el
 *      aviso debajo del encabezado si falla.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⟦ADR-0082 · 05/10/2026 · R4-UIM1⟧ · LO QUE ESTA PANTALLA TENÍA Y YA NO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Nada de lo de abajo se borra: se dice qué era y por qué deja de estar.**
 *
 * ① **«Columnas de errores visibles» y «Agregar columna»** (`ConfiguracionColumnas`,
 *   su estado `cargandoColumnas`/`errorColumnas` y su lectura). `R4-F3` RT 3
 *   (`PRD_R4.md:1816`): *«La lista de errores … está fija en el código y es la
 *   misma para los dos brazos. … La configuración de columnas de errores visibles
 *   y su tabla se quitan.»* y `R4-F3-CA-08` (`PRD_R4.md:1833`) lee el DOM entero
 *   para afirmar que no están.
 *   ⛔ **No se escondió con CSS, y la diferencia importa:** esconder la sección
 *   dejaba vivo el pedido `GET columnas` contra una ruta que `R4-AWM1` retiró —un
 *   `404` en cada carga— y `CA-08` igual lo habría encontrado en el DOM. Se fue
 *   el componente, su estado y su pedido. El rastro del borde está en
 *   `borde-metricas.ts`.
 *
 * ② ⟦el-icono-de-info-se-retira⟧ **El ícono de info por columna y el `<ol
 *   class="metricas__notas">` con las diez definiciones.** Eran el mecanismo de
 *   F4 para «qué significa cada número», y resolvían un defecto real —las
 *   definiciones eran inalcanzables sin mouse, y por eso dejaron de ser un
 *   `<span title>` para ser un `<button>` con la nota al pie—.
 *   ⛔ **R4 pone ese mismo contenido en otro lugar y con otros textos:**
 *   `PRD_R4.md:1686-1705` lo manda «Debajo del paginado de la Tabla 1, con ese
 *   título», con **diecisiete** entradas (no diez) y textos distintos, y
 *   `R4-F3-CA-45` los cita entre comillas y EN ORDEN. Mantener los dos dejaba
 *   DOS diccionarios en el DOM —uno con los textos viejos— y `CA-45` no habría
 *   podido distinguir cuál leía.
 *   ⭐ **Lo que el mecanismo viejo logró NO se pierde:** el diccionario de R4 es
 *   texto de verdad en el DOM, siempre visible, sin `:hover` ni `title` — que es
 *   exactamente la propiedad por la que el ícono había cambiado de etiqueta.
 *
 * ③ **El `<form class="metricas__formulario">` dentro de la página.** `R4-F3`
 *   §Periodos (`PRD_R4.md:1607`) lo pone en un popup. Los `id` de los cuatro
 *   campos (`#semana-nombre`, `#semana-desde`, `#semana-hasta`,
 *   `#semana-posicion`) **se conservan** aunque el formulario se haya mudado: son
 *   la forma en que los exámenes lo alcanzan, y renombrarlos habría sido un
 *   cambio sin requisito.
 *
 * ④ **`new Date(desde).toISOString()` al armar el cuerpo.** Ver §D3 abajo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS GUARDS DE LA ETAPA QUE VIVEN ACÁ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **D3 / B13 · LA PANTALLA NO CONVIERTE DÍAS.** ⛔⛔ Es el guard más fácil de
 * romper y el único cuya rotura **pasa en UTC y falla en Chile**. `desde`/`hasta`
 * llegan `aaaa-mm-dd` (texto) y se dibujan `dd-mm-aaaa` **reordenando el texto**
 * (`diaCorto`, en `formato-de-metrica.ts`, con la medición de las tres zonas); el
 * popup manda `aaaa-mm-dd` **tal como lo da `<input type="date">`**. ⛔ No hay un
 * `new Date` sobre una fecha de periodo en este archivo.
 * Lo que había (`:281-282`, `new Date(desde).toISOString()`) es la ventana que
 * `R4-AWM1` midió: el borde nuevo la RECHAZA con `400`, así que **Métricas no
 * podía cargar un periodo** hasta esta etapa.
 *
 * **B15 · LAS CELDAS NO RECALCULAN NADA.** Cada celda sale de
 * `formato-de-metrica.ts`, que espeja la función única del contrato. ⛔ Ni un
 * `Math.round`, ni una división, ni un `%` recalculado acá.
 *
 * **B16 · «Pidió humano» POR LA CAPACIDAD, NUNCA POR EL NOMBRE.** Ver
 * §`derivacionDelBrazoDeLaPantalla`, abajo, que nombra las DOS fuentes y por qué
 * se usan las dos. No hay ninguna comparación con `"puro"` ni con `"hibrido"`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ CÓMO SE MIDEN LAS CUATRO AUSENCIAS DE ARRIBA — Y POR QUÉ UN `grep` CRUDO
 *      LAS DA MAL, MEDIDO EL 05/10/2026
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⛔⛔ **La primera versión de este encabezado afirmaba `grep -c "new Date"` ⇒ 0,
 * `grep -c "Math\."` ⇒ 0 y `grep -c '=== "puro"'` ⇒ 0. LAS TRES ERAN FALSAS**, y
 * no porque el código las tuviera: porque **este mismo docblock nombra los
 * patrones**. Medido sobre el archivo entero:
 *
 *     grep -c "new Date"    src/metricas/pantalla.tsx ⇒ 7
 *     grep -c "Math\."      src/metricas/pantalla.tsx ⇒ 1
 *     grep -c "Descargando" src/metricas/pantalla.tsx ⇒ 4
 *
 * ⇒ **un chequeo de texto lee los comentarios como código.** Documentar bien una
 * ausencia es la forma más fácil de volver mentirosa su medición, y quien corra
 * el `grep` de arriba concluye que el guard está roto cuando está cumplido.
 *
 * ⭐ **La medición que sí contesta** borra comentarios antes de contar
 * (`/\*…\*\/` y `//…`), y sobre el CÓDIGO de este archivo da:
 *
 *     new Date 0 · Math. 0 · Descargando 0 · === "puro" 0 · === "hibrido" 0
 *     toISOString 0 · toLocaleDateString 0 · location.assign 0
 *
 * ⛔ **Y con su control de respuesta conocida en la misma corrida**, porque un
 * filtro que borre demasiado da CERO por la razón equivocada:
 *   · `src/alta.tsx` tiene un `new Date()` en código ⇒ el filtro cuenta **1**.
 *   · `src/boton-de-descarga.tsx` tiene el literal «Descargando…» en código ⇒ **1**.
 *   · El filtro sobre un `new Date` que está SÓLO en un comentario ⇒ **0**.
 * ⇒ el filtro distingue código de comentario en los tres sentidos.
 *
 * ⚠️ `formato-de-metrica.ts` **sí tiene 4 `Math.` en código**, y está bien: son
 * `Math.sign`/`Math.round`/`Math.abs` dentro de `redondearMitadArriba` y
 * `conPuntoDeMiles`, que son el cuerpo ESPEJADO de la función del contrato. B15
 * prohíbe una SEGUNDA regla de redondeo, no la única que hay.
 *
 * **D9 · PESTAÑAS DE ESTADO LOCAL.** `useState`, no el `hash`: cambiar de pestaña
 * no cambia la dirección y al recargar vuelve «Resultados» (`PRD_R4.md:1597`,
 * `R4-F3-CA-06`). ⛔ No se reusa `configuracion-del-chatbot/pestanas.tsx`, que
 * dibuja enlaces con `hash` y al recargar abriría «Errores de Meta».
 *
 * **A6 / `ADR-0386` · LA DESCARGA ES LA PRIMITIVA DE `R4-UI0`.** El literal
 * «Descargando…» vive en `BotonDeDescarga` y **no se repite acá**: esta pantalla
 * pasa el rótulo y la acción, y pinta el aviso donde `PRD_R4.md:1753` lo pone
 * («Debajo del encabezado»), que es justamente lo que la primitiva entrega por
 * `onError` en vez de dibujarlo adentro.
 *
 * **⑥ ESTADO VACÍO ≠ ERROR.** «No hay periodos cargados» es un resultado; un
 * fallo de lectura se dice «No se pudo hablar con el servidor.». Y un `null` de
 * porcentaje se dibuja «—», nunca «0 %».
 *
 * **⑦ PII.** Ni un teléfono, ni un nombre de persona, ni un dato con forma de
 * dato de persona. Los periodos se llaman «Met Semana 1», nunca como alguien.
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import { BotonDeDescarga } from "../boton-de-descarga.tsx"
import { CeldaFechaHora } from "../celda-fecha-hora.tsx"
import { useBordeMetricas } from "../contexto-borde-metricas.tsx"
import { Paginado } from "../paginado.tsx"
import type { SemanaRequestBody } from "@contracts/analytics-api/http.ts"
import { derivaAHumano, selloDelBrazo, type Brazo } from "../zonas.ts"
import { CUERPO_CONFLICTO_ITEM, CUERPO_CONFLICTO_POSICION } from "./borde-metricas.ts"
import { PopupDeMetricas } from "./popup.tsx"
import { diaCorto, type FilaDeErrores, type FilaDeMetricas } from "./formato-de-metrica.ts"
import type { ErrorsResponseBody, MetricsResponseBody } from "@contracts/analytics-api/http.ts"
import {
  COLUMNAS,
  COLUMNAS_DE_ERROR,
  COLUMNA_ACCIONES,
  COLUMNA_INTERVALO,
  COLUMNA_ITEM,
  DICCIONARIO,
  ENTRADA_PIDIO_HUMANO,
  GRUPO_INTENSIDAD,
  GRUPO_MENSAJES,
  GRUPO_PERSONAS,
  NOTA_DEL_BRAZO,
  NOTA_DE_LOS_ERRORES,
  TITULO_DEL_DICCIONARIO,
  TITULO_PIDIO_HUMANO,
  conteoDeError,
  leyendaDeErrores,
  leyendaDeResultados,
} from "./tablas.ts"

// ════════════════════════════════════════════════════════════════════════════
// LOS TEXTOS DE PANTALLA, TODOS EN UN LUGAR Y TODOS DEL PRD
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⛔ Constantes y no literales en el JSX, por el mismo motivo que
 * `TITULO_PIDIO_HUMANO`: varios los usan DOS veces —el rótulo de la pestaña y el
 * `aria-controls`, el texto del botón y su `disabled`— y dos cadenas iguales
 * escritas en dos lugares divergen en la primera corrección de copy.
 */
const PESTANA_RESULTADOS = "Resultados"
const PESTANA_ERRORES = "Errores de Meta"

const BOTON_CARGAR_PERIODO = "Cargar periodo"
const BOTON_CSV_RESULTADOS = "Exportar CSV de resultados"
const BOTON_CSV_ERRORES = "Exportar CSV de errores"

/** §Mientras carga y si algo falla (`PRD_R4.md:1746-1753`), textuales. */
const CARGANDO = "Cargando…"
const SIN_SERVIDOR = "No se pudo hablar con el servidor."
const SIN_PERIODOS = "No hay periodos cargados"
const FALLO_GUARDAR = "No se pudo guardar el periodo. Intenta de nuevo."
const FALLO_BORRAR = "No se pudo borrar el periodo. Intenta de nuevo."
const FALLO_DESCARGA = "No se pudo descargar el CSV. Intenta de nuevo."

/** §Periodos (`PRD_R4.md:1612-1618`), textuales. */
const CAMPO_ITEM = "Item (nombre del periodo)"
const CAMPO_DESDE = "Desde"
const CAMPO_HASTA = "Hasta"
const CAMPO_POSICION = "Posición"
const AYUDA_DEL_PERIODO =
  "Desde se cuenta desde las 00:00 y Hasta hasta las 23:59, en hora de Chile."
const ERROR_DE_FECHAS = "Desde tiene que ser igual o anterior a Hasta."

/** Los dos `409` (`PRD_R4.md:1619`), que son DOS textos a propósito. */
const CONFLICTO_DE_ITEM = "Ya hay un periodo con ese Item en este brazo."
const CONFLICTO_DE_POSICION = "Ya hay un periodo con esa Posición en este brazo."

const TITULO_CARGAR = "Cargar periodo"
const TITULO_EDITAR = "Editar periodo"
const BOTON_CARGAR = "Cargar"
const BOTON_GUARDAR = "Guardar"
const BOTON_CANCELAR = "Cancelar"
const BOTON_EDITAR_FILA = "Editar"
const BOTON_BORRAR_FILA = "Borrar"
const BOTON_CONFIRMAR_BORRADO = "Sí, borrar periodo"

/** §Borrar (`PRD_R4.md:1625`), textual. */
const TEXTO_DEL_BORRADO =
  "El periodo deja de aparecer en Métricas y en el CSV. Los mensajes y los participantes no cambian."

/**
 * `PRD_R4.md:1613`: *«Texto, obligatorio, hasta 100 caracteres: el campo no
 * acepta el carácter 101»*. `R4-F3-CA-49` lo ejerce con 101 caracteres.
 *
 * ⛔ Va como `maxLength` del `<input>` **y además se corta en el `onChange`**. El
 * atributo solo no alcanza: `maxLength` no frena un `value` puesto por programa
 * —que es lo que hace `escribir()` de los exámenes y lo que hace un pegado en
 * algunos navegadores—, y el campo quedaría con 101. Las dos mitades afirman lo
 * mismo por dos caminos.
 */
const MAXIMO_DEL_ITEM = 100

/** Diez periodos por página (`PRD_R4.md:1660`). */
const PERIODOS_POR_PAGINA = 10

// ════════════════════════════════════════════════════════════════════════════
// LA CAPACIDAD DE DERIVACIÓN (B16)
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⭐⭐ **B16 · «Pidió humano» CUELGA DE LA CAPACIDAD, Y HAY DOS FUENTES DE
 * CAPACIDAD. SE USAN LAS DOS.**
 *
 * `PRD_R1.md:194`, que `ADR-0304`-la-guarda cita textual: *«el comportamiento no
 * se ata al nombre del brazo, se ata a una capacidad declarada. El producto
 * nunca pregunta "¿este brazo se llama Híbrido?"; pregunta "¿este brazo tiene
 * derivación habilitada?"»*. ⛔ Ninguna comparación con `"puro"` ni `"hibrido"`
 * en este archivo.
 *
 * Las dos fuentes, y qué ve cada una:
 *
 *   ⓐ **LA DECLARACIÓN** · `derivaAHumano(brazo)` (`zonas.ts:211`), que lee
 *     `declarada.brazoHibrido` de `configuracion.json`. **Siempre se puede
 *     responder** y falla hacia MOSTRAR (sin declaración, `true`): *«una columna
 *     de más se ve; una métrica que desaparece sola, no»*. Es la fuente que
 *     `padron/tabla.tsx` usa, así que es la que hace que las DOS pantallas no
 *     puedan contestar distinto sobre el mismo brazo
 *     (`pruebas/r11-instancia-de-un-brazo.test.tsx` §(c) lo afirma por igualdad).
 *   ⓑ **LA RESPUESTA** · `fila.metrics.pidioHumano !== undefined`. El contrato
 *     hace la ausencia IMPOSIBLE de falsear: `PeriodMetricRow.metrics` es la
 *     unión `PeriodMetrics | PeriodMetricsSinDerivacion` y en la segunda
 *     `pidioHumano` es `?: never` (`ADR-0318`). Es la MISMA regla que el
 *     servidor, que es lo que el `guard` B16 pide. ⛔ **Pero sólo se puede
 *     responder si hay al menos una fila.**
 *
 * ⛔ **Por qué no alcanza ⓐ sola** — es el `rationale_hint` del guard, textual:
 * *«dejar `derivaAHumano(brazo)` … puede dibujar la columna vacía en un brazo
 * cuya respuesta no trae el campo»*. Con la declaración diciendo que sí y la
 * respuesta sin el campo, la columna se dibujaría con catorce «—» que parecen un
 * dato.
 *
 * ⛔ **Por qué no alcanza ⓑ sola:** sin filas no se puede responder, y
 * `ADR-0304`-la-guarda prohíbe el `?? true/false` que taparía el hueco. Además,
 * ⓑ sola desacopla esta pantalla del Padrón: un doble de examen que siempre trae
 * el campo haría aparecer la columna en un brazo declarado sin derivación.
 *
 * ⇒ **la columna se dibuja cuando las dos fuentes dicen que la capacidad existe**,
 * y cuando ⓑ no se puede responder decide ⓐ —que siempre puede—. **No hay un
 * `?? true` sobre una capacidad indeterminada:** lo indeterminado es ⓑ, y el
 * caso en que ⓑ calla es exactamente el caso en que no hay ninguna celda que
 * dibujar (`PRD_R4.md:1661`: sin periodos la tabla queda con una sola línea,
 * «No hay periodos cargados»).
 *
 * ⚠️ **El mismo booleano manda en los TRES lugares** —la columna, la entrada
 * `ENTRADA_PIDIO_HUMANO` del diccionario y `NOTA_DEL_BRAZO`—, que es lo que pide
 * `R4-F3-CA-11`/`CA-46`/`CA-47`. Tres decisiones separadas podrían dibujar la
 * nota de un brazo cuya columna no está.
 */
function capacidadEnLaRespuesta(items: readonly FilaDeMetricas[]): boolean | null {
  const primera = items[0]
  // Sin filas la respuesta no dice nada de la capacidad. No se inventa.
  if (primera === undefined) return null
  return primera.metrics.pidioHumano !== undefined
}

function derivacionDelBrazoDeLaPantalla(
  brazo: Brazo | null,
  items: readonly FilaDeMetricas[],
): boolean {
  const declarada = derivaAHumano(brazo)
  const enLaRespuesta = capacidadEnLaRespuesta(items)
  if (enLaRespuesta === null) return declarada
  return declarada && enLaRespuesta
}

// ════════════════════════════════════════════════════════════════════════════
// EL POPUP DE CARGAR / EDITAR UN PERIODO
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⛔ **LA VALIDACIÓN DE `Desde ≤ Hasta` ES UNA COMPARACIÓN DE TEXTO, Y ES
 * CORRECTA PORQUE EL FORMATO LO HACE CORRECTA.**
 *
 * `aaaa-mm-dd` tiene ancho fijo y los campos van de mayor a menor significancia,
 * así que el orden lexicográfico ES el orden cronológico: `"2025-01-20" <=
 * "2025-01-26"`. ⛔ Un `new Date(desde) <= new Date(hasta)` daría el MISMO
 * resultado aquí —los dos se parsean como medianoche UTC— pero abriría la puerta
 * a que alguien reuse esos `Date` para el cuerpo, que es el defecto de `:281-282`
 * que esta etapa cierra. **No se construye un `Date` ni para comparar.**
 *
 * ⚠️ Con un campo vacío devuelve `true`: «falta un campo» es otro motivo de
 * deshabilitado (`R4-F3-CA-50`) y mezclarlos mostraría el error de fechas antes
 * de que haya fechas.
 */
function elIntervaloEsValido(desde: string, hasta: string): boolean {
  if (desde === "" || hasta === "") return true
  return desde <= hasta
}

/**
 * `PRD_R4.md:1615`: *«Número entero, obligatorio, desde 1»*. `R4-F3-CA-54` lo
 * ejerce con `0`.
 *
 * ⛔ `Number.isInteger` y no `Number.isFinite`: `1.5` es finito y no es un
 * entero, y el servidor lo rechaza con `400` (`R4-F3` RT 1). ⛔ Y se mira el
 * TEXTO antes de convertir: `Number("")` es `0`, que pasaría por «entero» y
 * dejaría «Cargar» habilitado con el campo vacío.
 */
function laPosicionEsValida(posicion: string): boolean {
  if (posicion.trim() === "") return false
  const numero = Number(posicion)
  return Number.isInteger(numero) && numero >= 1
}

/**
 * El aviso que corresponde a un fallo de guardado.
 *
 * ⛔ **El `409` se distingue por el CUERPO y no por el código**, porque el código
 * es el mismo para las dos restricciones. `contracts/analytics-api/http.ts:230`:
 * *«la pantalla tiene que poder distinguir cuál de las dos restricciones de
 * unicidad se rompió. Un solo "conflicto" la deja mostrando el aviso
 * equivocado.»*
 *
 * ⚠️ Se compara con `includes` y no con `===`: `mensajeDeError` del borde puede
 * envolver el cuerpo del servidor. Los dos textos del contrato no son prefijo
 * uno del otro, así que `includes` no puede confundirlos.
 *
 * ⛔ **Cualquier otro fallo —`400`, `404`, `500`, la red caída— es
 * `FALLO_GUARDAR`**, el único texto que `PRD_R4.md:1749` le da. No se filtra el
 * mensaje crudo del servidor a la pantalla: puede citar una fila de la base
 * (`ADR-0201`).
 */
function avisoDeGuardado(causa: unknown): string {
  const mensaje = causa instanceof Error ? causa.message : String(causa)
  if (mensaje.includes(CUERPO_CONFLICTO_ITEM)) return CONFLICTO_DE_ITEM
  if (mensaje.includes(CUERPO_CONFLICTO_POSICION)) return CONFLICTO_DE_POSICION
  return FALLO_GUARDAR
}

function PopupDePeriodo(props: {
  readonly borde: ReturnType<typeof useBordeMetricas>
  readonly miga: string
  /** `null` ⇒ cargar uno nuevo; una `Semana` ⇒ editar ésa. */
  readonly editando: SemanaRequestBody | null
  readonly onCerrar: () => void
  readonly onGuardado: () => void
}): JSX.Element {
  const { borde, editando } = props
  const edita = editando !== null

  const [nombre, setNombre] = useState(editando?.nombre ?? "")
  /*
   * ⛔⛔ **EL VALOR INICIAL ES EL DÍA DEL CABLE, RECORTADO A DIEZ CARACTERES, SIN
   * UN `Date` EN EL MEDIO.**
   *
   * `<input type="date">` exige `value="aaaa-mm-dd"`, que es EXACTAMENTE lo que
   * el cable manda (`DiaDeChile`). El `slice(0, 10)` es por si un periodo
   * guardado antes de R4 llegara con hora pegada: se queda con el día y no lo
   * corre. ⛔ `new Date(editando.desde).toISOString().slice(0, 10)` —la salida
   * cómoda— devuelve el día anterior en cualquier zona al oeste de UTC, así que
   * «Editar» en Chile abriría el popup con el periodo corrido un día y
   * «Guardar» lo GUARDARÍA corrido. `R4-F3-CA-65` fija Desde 27-01-2025 para
   * «Met Editar».
   */
  const [desde, setDesde] = useState(editando === null ? "" : editando.desde.slice(0, 10))
  const [hasta, setHasta] = useState(editando === null ? "" : editando.hasta.slice(0, 10))
  const [posicion, setPosicion] = useState(editando === null ? "" : String(editando.posicion))
  const [enCurso, setEnCurso] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)

  const intervaloValido = elIntervaloEsValido(desde, hasta)
  const faltaUnCampo =
    nombre.trim() === "" || desde === "" || hasta === "" || posicion.trim() === ""

  /**
   * `PRD_R4.md:1617`: *«"Cargar" está deshabilitado mientras falte un campo,
   * mientras Desde sea posterior a Hasta o mientras Posición no sea un entero
   * desde 1»* — y `PRD_R4.md:1748` agrega el cuarto motivo: **mientras el pedido
   * está en curso** (`R4-F3-CA-61`).
   *
   * ⛔ Los cuatro motivos en UNA expresión y no cuatro `disabled` encadenados:
   * así no puede quedar uno sin cubrir cuando alguien agregue un campo.
   */
  const puedeGuardar = !enCurso && !faltaUnCampo && intervaloValido && laPosicionEsValida(posicion)

  const guardar = async (): Promise<void> => {
    if (!puedeGuardar) return
    setEnCurso(true)
    setAviso(null)

    /*
     * ⭐⭐ **D3 · EL CUERPO LLEVA EL DÍA TAL COMO LO DIO EL `<input type="date">`.**
     *
     * ⟦ADR-0082 · 05/10/2026 · R4-UIM1⟧ Acá decía:
     *
     *   // Los `Date` cruzan como string ISO-8601 con offset (⟦un-date-no-cruza-json⟧).
     *   const semana: SemanaRequestBody = {
     *     nombre: nombre.trim(),
     *     desde: new Date(desde).toISOString(),
     *     hasta: new Date(hasta).toISOString(),
     *     posicion: posicionNumerica,
     *   }
     *
     * ⛔ **Era correcto cuando `Semana.desde` era un `Date` y dejó de serlo con
     * `R4-CAN1`** (`R4-F3` RT 1, `PRD_R4.md:1814`): hoy es `DiaDeChile`, un día
     * `aaaa-mm-dd`. ⭐ **Y `R4-AWM1` midió la consecuencia:** el borde nuevo
     * RECHAZA con `400` un `desde` que no tenga la forma del día, así que
     * mientras ese `toISOString()` estuviera acá **Métricas no podía cargar
     * ningún periodo**. Esta línea es la que cierra esa ventana.
     *
     * ⚠️ `nombre.trim()` se conserva: `PRD_R4.md:1613` dice *«Los espacios de los
     * bordes no cuentan»* y la base compara el `Item` sin ellos (`R4-F3` RT 1).
     */
    const semana: SemanaRequestBody = {
      nombre: nombre.trim(),
      desde,
      hasta,
      posicion: Number(posicion),
    }

    try {
      if (editando === null) {
        await borde.cargarSemana(semana)
      } else {
        await borde.editarSemana(semana)
      }
      props.onGuardado()
    } catch (causa: unknown) {
      /*
       * `PRD_R4.md:1749`: *«El popup sigue abierto, con lo escrito, y muestra
       * "No se pudo guardar el periodo. Intenta de nuevo."; no se guarda nada»*.
       * ⛔ No se llama a `onCerrar()` ni se limpian los campos: lo escrito queda.
       */
      setAviso(avisoDeGuardado(causa))
    } finally {
      setEnCurso(false)
    }
  }

  /** La clase de un campo de fecha en error (`R4-F3-CA-52`: «borde de error»). */
  const claseDeFecha = intervaloValido ? "" : " metricas__campo--error"

  return (
    <PopupDeMetricas
      titulo={edita ? TITULO_EDITAR : TITULO_CARGAR}
      miga={props.miga}
      onCerrar={props.onCerrar}
      /*
       * ⭐ `R4-F3-CA-57` / `PRD_R4.md:1619`: *«"Cancelar", Escape o tocar fuera
       * del popup lo cierran sin guardar»*. El popup de BORRAR no lleva este
       * prop: ver su propio comentario.
       */
      cerrarAlTocarFuera
    >
      <form
        className="metricas__popup-formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          void guardar()
        }}
      >
        <div className="metricas__popup-campos">
          <div className="metricas__campo">
            <label htmlFor="semana-nombre">{CAMPO_ITEM}</label>
            {/*
              ⭐ `R4-F3-CA-65` · El Item se ve DESHABILITADO al editar.
              `Semana` no tiene `id` en el contrato: se identifica por `nombre`,
              que es a la vez la clave del `PUT` y el `Item` de la fila. Con el
              campo escribible, «editar» permitía mandar el `PUT` contra una
              clave nueva y la fila original quedaba intacta sin que nada lo
              dijera. Renombrar un periodo es otra operación y R4 no la tiene.

              ⚠️ `disabled` y no `readOnly`: el campo se sale del tabulador, que
              es lo que el boceto muestra. Su valor igual viaja en el cuerpo
              porque `guardar()` lo arma desde el estado, no desde un `FormData`.
            */}
            <input
              id="semana-nombre"
              type="text"
              value={nombre}
              maxLength={MAXIMO_DEL_ITEM}
              onChange={(evento) => setNombre(evento.target.value.slice(0, MAXIMO_DEL_ITEM))}
              disabled={enCurso || edita}
            />
          </div>

          <div className="metricas__campo">
            <label htmlFor="semana-desde">{CAMPO_DESDE}</label>
            <input
              id="semana-desde"
              type="date"
              className={`metricas__fecha${claseDeFecha}`}
              value={desde}
              onChange={(evento) => setDesde(evento.target.value)}
              disabled={enCurso}
              aria-invalid={!intervaloValido}
              aria-describedby={intervaloValido ? undefined : "semana-error-de-fechas"}
            />
          </div>

          <div className="metricas__campo">
            <label htmlFor="semana-hasta">{CAMPO_HASTA}</label>
            <input
              id="semana-hasta"
              type="date"
              className={`metricas__fecha${claseDeFecha}`}
              value={hasta}
              onChange={(evento) => setHasta(evento.target.value)}
              disabled={enCurso}
              aria-invalid={!intervaloValido}
              aria-describedby={intervaloValido ? undefined : "semana-error-de-fechas"}
            />
          </div>

          <div className="metricas__campo">
            <label htmlFor="semana-posicion">{CAMPO_POSICION}</label>
            {/*
              `min={1}` acompaña a la validación, no la reemplaza: el atributo
              no frena un valor puesto por programa ni un `0` tecleado en los
              navegadores que no validan al vuelo. Quien decide es
              `laPosicionEsValida`.
            */}
            <input
              id="semana-posicion"
              type="number"
              min={1}
              step={1}
              value={posicion}
              onChange={(evento) => setPosicion(evento.target.value)}
              disabled={enCurso}
            />
          </div>
        </div>

        {/*
          ⭐ `R4-F3-CA-51` · *«debajo de la fila de campos»*, textual
          (`PRD_R4.md:1616`). Va DESPUÉS de `metricas__popup-campos` y no al lado
          de cada campo: el PRD lo pone una vez, debajo de la fila.
        */}
        {!intervaloValido && (
          <p className="metricas__error" id="semana-error-de-fechas" role="alert">
            {ERROR_DE_FECHAS}
          </p>
        )}

        <p className="metricas__ayuda">{AYUDA_DEL_PERIODO}</p>

        {aviso !== null && (
          <p className="metricas__error" role="alert">
            {aviso}
          </p>
        )}

        <footer className="dialogo__acciones">
          {/*
            ⛔ El orden es «Cancelar» y después «Cargar»/«Guardar»
            (`PRD_R4.md:1617`), pero el `type="submit"` va primero en el DOM para
            que Enter en un campo dispare el guardado y no el cierre. El orden
            VISUAL lo pone `.dialogo__acciones` con `flex-direction: row-reverse`
            en la hoja, que es como lo hacen los otros cuatro modales.
          */}
          <button type="submit" disabled={!puedeGuardar}>
            {edita ? BOTON_GUARDAR : BOTON_CARGAR}
          </button>
          <button type="button" onClick={props.onCerrar} disabled={enCurso}>
            {BOTON_CANCELAR}
          </button>
        </footer>
      </form>
    </PopupDeMetricas>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// EL POPUP DE BORRAR
// ════════════════════════════════════════════════════════════════════════════

function PopupDeBorrado(props: {
  readonly borde: ReturnType<typeof useBordeMetricas>
  readonly miga: string
  readonly nombre: string
  readonly onCerrar: () => void
  readonly onBorrado: () => void
}): JSX.Element {
  const [enCurso, setEnCurso] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)

  const borrar = async (): Promise<void> => {
    if (enCurso) return
    setEnCurso(true)
    setAviso(null)
    try {
      await props.borde.borrarSemana(props.nombre)
      props.onBorrado()
    } catch {
      /*
       * `PRD_R4.md:1750`: *«El popup sigue abierto y muestra "No se pudo borrar
       * el periodo. Intenta de nuevo."; el periodo sigue en las tablas»*
       * (`R4-F3-CA-74`). ⛔ No se llama a `onBorrado()`, que es lo que refresca
       * las tablas: la fila sigue donde estaba porque nada se volvió a leer.
       *
       * ⚠️ `catch` sin binding: no hay nada que mirar. El borrado tiene UN solo
       * aviso para cualquier fallo y el mensaje del servidor no se filtra.
       */
      setAviso(FALLO_BORRAR)
    } finally {
      setEnCurso(false)
    }
  }

  /*
   * ⛔⛔ **ESTE POPUP NO LLEVA `cerrarAlTocarFuera`, Y ES UNA DECISIÓN CON DOS
   * FUENTES QUE COINCIDEN.** (Va acá y no entre los atributos: un comentario en
   * la lista de atributos de un JSX es un error de sintaxis, `TS1005`, y lo
   * medí.)
   *
   * ① `PRD_R4.md:1625`, textual: *«Con "Cancelar" o Escape no cambia nada»* —
   *   nombra DOS salidas y **no** «tocar fuera», al contrario de `:1619` para el
   *   popup de periodo, que nombra las tres.
   * ② Y `banco-de-contenido/dialogo.tsx:16-20` dice por qué: *«en el de borrado
   *   significaría que el gesto más descuidado posible está a un pixel de la
   *   confirmación»*.
   *
   * ⇒ no es una omisión: es la diferencia entre los dos popups, pedida por el
   * PRD y razonada en el repo. `R4-F3-CA-72` y `CA-73` ejercen las dos salidas
   * que sí tiene.
   *
   * ⭐ Y el título: `R4-F3-CA-71` / `PRD_R4.md:1625` lo fijan textual —*«¿Borrar
   * el periodo «[Item]»?»*— con las comillas angulares alrededor del Item.
   */
  return (
    <PopupDeMetricas
      titulo={`¿Borrar el periodo «${props.nombre}»?`}
      miga={props.miga}
      onCerrar={props.onCerrar}
    >
      <p>{TEXTO_DEL_BORRADO}</p>
      {/* `PRD_R4.md:1625`, fila «ayuda»: *«Afecta solo a KAI · Brazo X.»* — el sello, no un literal. */}
      <p className="metricas__ayuda">{`Afecta solo a ${props.miga}.`}</p>

      {aviso !== null && (
        <p className="metricas__error" role="alert">
          {aviso}
        </p>
      )}

      <footer className="dialogo__acciones">
        {/* `R4-F3-CA-61` · deshabilitado mientras su pedido está en curso. */}
        <button type="button" onClick={() => void borrar()} disabled={enCurso}>
          {BOTON_CONFIRMAR_BORRADO}
        </button>
        <button type="button" onClick={props.onCerrar} disabled={enCurso}>
          {BOTON_CANCELAR}
        </button>
      </footer>
    </PopupDeMetricas>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// LA PANTALLA
// ════════════════════════════════════════════════════════════════════════════

/**
 * El intervalo de una fila, en una celda y en una línea.
 *
 * ⭐⭐ **USA `CeldaFechaHora` DE `R4-UI0`, UNA VEZ POR DÍA, Y NO ESCRIBE UNA
 * TERCERA CELDA.** `R4-UIE1` ensanchó la primitiva para que acepte
 * `Date | string` precisamente para esto (`celda-fecha-hora.tsx:63-67`: *«`R4-UIM1`
 * necesita esta misma celda para sus fechas de periodo, que también llegan como
 * texto del cable»*), y `ADR-0386` prohíbe una segunda definición de la celda.
 *
 * ⛔⛔ **PERO MEDIDO HOY: LA PRIMITIVA PARTE SU TEXTO EN EL PRIMER ESPACIO.**
 * `celda-fecha-hora.tsx:113-126` hace `texto.indexOf(" ")` y dibuja
 * `{dia}<br />{hora}` — es una celda de «fecha arriba, hora abajo». Con el
 * intervalo entero (`"06-07-2026 – 12-07-2026"`) el primer espacio cae después
 * del primer día, así que dibujaría:
 *
 *     06-07-2026
 *     – 12-07-2026
 *
 * y su `textContent` sería `"06-07-2026– 12-07-2026"` — **sin el espacio antes
 * del guion**, que es lo que `R4-F3-CA-14` cita («06-07-2026 – 12-07-2026»).
 * Pasarle el intervalo completo rompe el criterio.
 *
 * ⇒ se le pasa **un día por vez**, que es un texto SIN espacio y cae en la rama
 * que la propia primitiva documenta (*«Un texto SIN espacio … se dibuja entero en
 * la primera línea»*), y el « – » se pone entre las dos. El `textContent` de la
 * celda queda `"06-07-2026 – 12-07-2026"`, con su espacio.
 *
 * ⚠️ **El costo, dicho y resuelto:** en la rama sin espacio la primitiva igual
 * emite su `<br />` con una segunda línea vacía, así que la celda mediría dos
 * renglones en una tabla de diecisiete columnas. Se neutraliza en la hoja, en la
 * sección «Métricas» y acotado a esta clase:
 * `.metricas__intervalo .celda-fecha-hora br { display: none }`. **No se toca la
 * primitiva ni su regla**: el `<br>` sigue haciendo su trabajo en Exportación.
 *
 * ⛔ **Y lo que NO se hizo:** escribir una tercera celda, ni formatear el
 * intervalo como texto pelado sin la primitiva. La primitiva formatea el día de
 * Chile en un solo lugar y es el único lugar donde una corrección llega a las
 * tres pantallas.
 */
function CeldaDelIntervalo(props: {
  readonly desde: string
  readonly hasta: string
}): JSX.Element {
  return (
    <span className="metricas__intervalo">
      <CeldaFechaHora fecha={diaCorto(props.desde)} />
      {" – "}
      <CeldaFechaHora fecha={diaCorto(props.hasta)} />
    </span>
  )
}

type PropiedadesDeMetricas = {
  /**
   * ⭐ El brazo de la dirección, y de él salen DOS cosas: el SELLO que nombran
   * las dos leyendas, los dos popups y la ayuda del borrado
   * (`selloDelBrazo`), y la mitad declarada de la capacidad de derivación
   * (`derivaAHumano`).
   *
   * ⚠️ **Ausente ⇒ `derivaAHumano(null)` es `true` y el sello no se puede armar.**
   * Es el comportamiento de quien monte esta pantalla suelta, fuera de una ruta
   * de brazo. La dirección de falla de la capacidad es la de siempre y es
   * deliberada: mostrar de más se ve; esconder una métrica en silencio, no.
   */
  readonly brazo?: Brazo
}

export function PantallaMetricas(props: PropiedadesDeMetricas = {}): JSX.Element {
  const brazoDeLaPantalla = props.brazo
  /*
   * ⛔ ⟦ADR-0082 · 05/10/2026 · R4-UIM1⟧ — ACÁ ESTABA
   * `const config = useConfiguracion()`, y su único uso era `config.pageSize`
   * como tamaño de las dos tablas. **Se retira porque `PRD_R4.md:1660` fija el
   * tamaño de ESTA pantalla en diez** (`PERIODOS_POR_PAGINA`, `R4-F3-CA-13`:
   * «Página 1 de 2» con diez filas en la primera página), y leer la escala de la
   * instancia dejaría a `CA-13` dependiendo de un `configuracion.json`.
   *
   * ⚠️ `ProveedorConfiguracion` sigue envolviendo a esta pantalla en `App.tsx` y
   * en los exámenes: lo que se fue es el CONSUMO, no el proveedor. Montar la
   * pantalla sin él ahora funciona, que es más permisivo que antes y no menos.
   */
  const borde = useBordeMetricas()

  /**
   * ⭐ **D9 · LA PESTAÑA ES ESTADO LOCAL.** `useState` y no el `hash`: cambiar de
   * pestaña no cambia la dirección (`PRD_R4.md:1597`) y al recargar vuelve
   * «Resultados» (`R4-F3-CA-06`), porque un `useState` arranca en su valor
   * inicial en cada montaje.
   *
   * ⛔ **No se reusa `configuracion-del-chatbot/pestanas.tsx`**: dibuja `<a>` con
   * `hash`, así que recargar sobre `#/…/metricas/errores` abriría «Errores de
   * Meta» — lo contrario de `CA-06`.
   */
  const [pestana, setPestana] = useState<"resultados" | "errores">("resultados")

  // ── Tabla 1 ───────────────────────────────────────────────────────────────
  const [paginaDeMetricas, setPaginaDeMetricas] = useState(1)
  const [metricas, setMetricas] = useState<MetricsResponseBody>({ items: [], total: 0 })
  const [cargandoMetricas, setCargandoMetricas] = useState(true)
  const [falloMetricas, setFalloMetricas] = useState(false)

  // ── Tabla 2 ───────────────────────────────────────────────────────────────
  const [paginaDeErrores, setPaginaDeErrores] = useState(1)
  const [errores, setErrores] = useState<ErrorsResponseBody>({ items: [], total: 0 })
  const [cargandoErrores, setCargandoErrores] = useState(true)
  const [falloErrores, setFalloErrores] = useState(false)

  // ── Los popups ────────────────────────────────────────────────────────────
  const [popupDePeriodo, setPopupDePeriodo] = useState<"cerrado" | "abierto">("cerrado")
  const [editando, setEditando] = useState<SemanaRequestBody | null>(null)
  const [porBorrar, setPorBorrar] = useState<string | null>(null)

  /** El aviso de descarga, debajo del encabezado (`PRD_R4.md:1753`). */
  const [falloDescarga, setFalloDescarga] = useState(false)

  /** Reloj de refresco: cada escritura re-lee las dos tablas. */
  const [recarga, setRecarga] = useState(0)

  /**
   * ⭐ **DIEZ POR PÁGINA, Y NO `config.pageSize`.** `PRD_R4.md:1660` lo fija para
   * esta pantalla: *«Diez periodos por página»*, y `R4-F3-CA-13` lo ejerce
   * («Página 1 de 2» con diez filas en la primera). `config.pageSize` es la
   * escala de la instancia y la usan las pantallas cuyo PRD no fija una.
   *
   * ⚠️ Las DOS tablas usan el mismo tamaño y paginan POR SEPARADO (guard ④):
   * `PRD_R4.md:1726` dice que la Tabla 2 va *«en el mismo orden y con el mismo
   * paginado que la Tabla 1»*, que es el mismo TAMAÑO, no la misma página.
   */
  const tamanoDePagina = PERIODOS_POR_PAGINA

  /** Cada cambio de página o refresco es una CONSULTA NUEVA al borde. */
  useEffect(() => {
    let cancelado = false
    setCargandoMetricas(true)
    setFalloMetricas(false)

    void borde
      .metricas({ page: paginaDeMetricas, pageSize: tamanoDePagina })
      .then((pagina) => {
        if (cancelado) return
        setMetricas(pagina)
        setCargandoMetricas(false)
      })
      .catch(() => {
        if (cancelado) return
        /*
         * `PRD_R4.md:1747`: *«si ya había filas a la vista, quedan como
         * estaban»*. ⛔ Por eso NO se hace `setMetricas({ items: [], total: 0 })`
         * en el camino de error: la página anterior se conserva y el aviso se
         * dibuja junto a ella.
         */
        setFalloMetricas(true)
        setCargandoMetricas(false)
      })

    return () => {
      cancelado = true
    }
  }, [borde, paginaDeMetricas, tamanoDePagina, recarga])

  useEffect(() => {
    let cancelado = false
    setCargandoErrores(true)
    setFalloErrores(false)

    void borde
      .errores({ page: paginaDeErrores, pageSize: tamanoDePagina })
      .then((pagina) => {
        if (cancelado) return
        setErrores(pagina)
        setCargandoErrores(false)
      })
      .catch(() => {
        if (cancelado) return
        setFalloErrores(true)
        setCargandoErrores(false)
      })

    return () => {
      cancelado = true
    }
  }, [borde, paginaDeErrores, tamanoDePagina, recarga])

  const refrescar = (): void => setRecarga((valor) => valor + 1)

  /**
   * El sello del brazo, o un vacío si la pantalla se montó fuera de una ruta de
   * brazo.
   *
   * ⛔ **Ni un «KAI» literal acá.** `selloDelBrazo()` lo arma del nombre de la
   * instancia y del brazo (`zonas.ts:254`), que es lo que el gate de `F4-CA-15`
   * de `R4-G0` exige. Un «KAI · Brazo Híbrido» escrito a mano sería correcto en
   * la instancia de prueba y falso en cualquier otra.
   */
  const sello = brazoDeLaPantalla === undefined ? "" : selloDelBrazo(brazoDeLaPantalla)

  /** B16 · el único booleano de la capacidad, para la columna, el diccionario y la nota. */
  const deriva = derivacionDelBrazoDeLaPantalla(brazoDeLaPantalla ?? null, metricas.items)

  const columnas = deriva ? COLUMNAS : COLUMNAS.filter((c) => c.titulo !== TITULO_PIDIO_HUMANO)
  const diccionario = deriva
    ? DICCIONARIO
    : DICCIONARIO.filter((e) => e.entrada !== ENTRADA_PIDIO_HUMANO)

  /**
   * Los tres grupos con cuántas columnas abarca cada uno, para el nivel de
   * ARRIBA del encabezado.
   *
   * ⛔ **El `colSpan` se CUENTA sobre las columnas que se van a dibujar, no se
   * escribe a mano.** Un `colSpan={7}` literal en «Personas» quedaría en 7 en el
   * brazo sin derivación, donde son 6 (`R4-F3-CA-11`), y el nivel de arriba se
   * desalinearía del de abajo sin que nada se ponga rojo: `tsc` no cuenta
   * celdas y `happy-dom` no hace layout.
   */
  const grupos: readonly { readonly rotulo: string; readonly cuantas: number }[] = [
    GRUPO_MENSAJES,
    GRUPO_PERSONAS,
    GRUPO_INTENSIDAD,
  ].map((rotulo) => ({
    rotulo,
    cuantas: columnas.filter((c) => c.grupo === rotulo).length,
  }))

  const abrirCarga = (): void => {
    setEditando(null)
    setPopupDePeriodo("abierto")
  }

  const abrirEdicion = (fila: FilaDeMetricas): void => {
    setEditando(fila.periodo)
    setPopupDePeriodo("abierto")
  }

  const cerrarPopupDePeriodo = (): void => {
    setPopupDePeriodo("cerrado")
    setEditando(null)
  }

  /** `true` cuando la tabla tiene que dibujar su línea única en lugar de filas. */
  const unaSolaLinea = (cargando: boolean, fallo: boolean, hayFilas: boolean): string | null => {
    if (cargando) return CARGANDO
    if (fallo && !hayFilas) return SIN_SERVIDOR
    if (!hayFilas) return SIN_PERIODOS
    return null
  }

  const lineaDeMetricas = unaSolaLinea(cargandoMetricas, falloMetricas, metricas.items.length > 0)
  const lineaDeErrores = unaSolaLinea(cargandoErrores, falloErrores, errores.items.length > 0)

  const enResultados = pestana === "resultados"

  return (
    <section className="metricas" aria-labelledby="metricas-titulo">
      <header className="metricas__encabezado">
        <h1 id="metricas-titulo">Métricas</h1>
        <button type="button" onClick={abrirCarga}>
          {BOTON_CARGAR_PERIODO}
        </button>
        {/*
          ⭐ **A6 · EL BOTÓN DE DESCARGA ES LA PRIMITIVA, Y EL RÓTULO SALE DE LA
          PESTAÑA.** `PRD_R4.md:1596` y `R4-F3-CA-07`: «Exportar CSV de
          resultados» con «Resultados» activa y «Exportar CSV de errores» con
          «Errores de Meta» activa.

          ⛔ **«Descargando…» NO se escribe acá.** Lo trae `BotonDeDescarga`
          junto con el `disabled` y la guarda del doble toque; esta pantalla pasa
          el rótulo y la acción. Reimplementar el literal sería la segunda copia
          que `ADR-0386` cierra, y divergiría del de Exportación y Conversaciones
          en la primera corrección.

          ⛔ **La `key` fuerza un botón NUEVO por pestaña, y hace falta.**
          `BotonDeDescarga` guarda su `cargando` en estado propio: sin la `key`,
          cambiar de pestaña con una descarga en vuelo dejaría el botón de la
          otra pestaña deshabilitado diciendo «Descargando…» por un pedido que ya
          no es el suyo.
        */}
        <BotonDeDescarga
          key={enResultados ? "resultados" : "errores"}
          rotulo={enResultados ? BOTON_CSV_RESULTADOS : BOTON_CSV_ERRORES}
          accion={() => {
            setFalloDescarga(false)
            return enResultados
              ? borde.descargarCsvDeResultados()
              : borde.descargarCsvDeErrores()
          }}
          onError={() => setFalloDescarga(true)}
        />
      </header>

      {/*
        `PRD_R4.md:1753`: *«Debajo del encabezado, el aviso "No se pudo descargar
        el CSV. Intenta de nuevo."»* (`R4-F3-CA-88`). Va acá, fuera del
        `<header>` y antes de las pestañas, que es «debajo del encabezado».
      */}
      {falloDescarga && (
        <p className="metricas__error" role="alert">
          {FALLO_DESCARGA}
        </p>
      )}

      {/*
        ⭐ **LAS DOS PESTAÑAS.** `role="tablist"` con dos `role="tab"`:
        `aria-selected` es lo que un lector de pantalla anuncia, y sin él dos
        botones seguidos no se distinguen de dos acciones.

        ⛔ Son `<button>` y no `<a>`: un enlace cambia la dirección, que es lo que
        D9 prohíbe.
      */}
      <div className="metricas__pestanas" role="tablist" aria-label="Pestañas de Métricas">
        <button
          type="button"
          role="tab"
          id="metricas-pestana-resultados"
          aria-selected={enResultados}
          aria-controls="metricas-panel-resultados"
          className={`metricas__pestana${enResultados ? " metricas__pestana--activa" : ""}`}
          onClick={() => setPestana("resultados")}
        >
          {PESTANA_RESULTADOS}
        </button>
        <button
          type="button"
          role="tab"
          id="metricas-pestana-errores"
          aria-selected={!enResultados}
          aria-controls="metricas-panel-errores"
          className={`metricas__pestana${enResultados ? "" : " metricas__pestana--activa"}`}
          onClick={() => setPestana("errores")}
        >
          {PESTANA_ERRORES}
        </button>
      </div>

      {/* ── Pestaña «Resultados» ──────────────────────────────────────────── */}
      {enResultados && (
        <div
          className="metricas__panel"
          role="tabpanel"
          id="metricas-panel-resultados"
          aria-labelledby="metricas-pestana-resultados"
        >
          {/*
            ⛔ **EL AVISO DE FALLO VA ARRIBA DE LA TABLA Y NO EN LUGAR DE ELLA**
            cuando ya había filas: `PRD_R4.md:1747` dice *«si ya había filas a la
            vista, quedan como estaban»*. Con la tabla vacía, el texto va
            ADENTRO, como la línea única que el PRD pide (`:1661`).
          */}
          {falloMetricas && metricas.items.length > 0 && (
            <p className="metricas__error" role="alert">
              {SIN_SERVIDOR}
            </p>
          )}

          <div className="tabla-rodante">
            <table className="metricas__tabla">
              <caption className="metricas__caption">{leyendaDeResultados(sello)}</caption>
              {/*
                ⭐⭐ **EL ENCABEZADO DE DOS NIVELES** (`PRD_R4.md:1639`,
                `R4-F3-CA-10`).

                Arriba, los tres grupos con su `colSpan`; Item, Desde – Hasta y
                Acciones **no pertenecen a ningún grupo** (`PRD_R4.md:1642-1648`
                les pone «—»), así que en el nivel de arriba les corresponde su
                propia celda abarcando las dos filas (`rowSpan={2}`) y no un
                grupo inventado.

                ⚠️ Las celdas de `rowSpan` van en la fila de ARRIBA y se SALTEAN
                en la de abajo: repetirlas abajo dibujaría «Item» dos veces.
              */}
              <thead>
                <tr>
                  <th scope="col" rowSpan={2}>
                    {COLUMNA_ITEM}
                  </th>
                  <th scope="col" rowSpan={2}>
                    {COLUMNA_INTERVALO}
                  </th>
                  {grupos.map((grupo) => (
                    <th key={grupo.rotulo} scope="colgroup" colSpan={grupo.cuantas}>
                      {grupo.rotulo}
                    </th>
                  ))}
                  <th scope="col" rowSpan={2}>
                    {COLUMNA_ACCIONES}
                  </th>
                </tr>
                <tr>
                  {columnas.map((columna) => (
                    <th key={columna.titulo} scope="col">
                      {columna.titulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineaDeMetricas !== null ? (
                  <tr>
                    {/*
                      `PRD_R4.md:1661`: *«Sin periodos, la tabla queda con una
                      sola línea»*. El `colSpan` se cuenta: dos columnas de
                      periodo + las métricas + Acciones.
                    */}
                    <td colSpan={columnas.length + 3} className="metricas__vacio">
                      {lineaDeMetricas}
                    </td>
                  </tr>
                ) : (
                  metricas.items.map((fila) => (
                    <tr key={fila.periodo.nombre}>
                      <td>{fila.periodo.nombre}</td>
                      <td>
                        <CeldaDelIntervalo desde={fila.periodo.desde} hasta={fila.periodo.hasta} />
                      </td>
                      {columnas.map((columna) => (
                        <td key={columna.titulo} className="metricas__numero">
                          {columna.render(fila.metrics)}
                        </td>
                      ))}
                      {/*
                        `R4-F3-CA-14`: *«y en Acciones "Editar" y "Borrar"»*. En
                        su columna, al final, y no pegados al Item como antes.
                      */}
                      <td className="metricas__acciones">
                        <button type="button" onClick={() => abrirEdicion(fila)}>
                          {BOTON_EDITAR_FILA}
                        </button>
                        <button type="button" onClick={() => setPorBorrar(fila.periodo.nombre)}>
                          {BOTON_BORRAR_FILA}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Paginado
            etiqueta="métricas"
            pagina={paginaDeMetricas}
            total={metricas.total}
            tamanoDePagina={tamanoDePagina}
            unidad={{ singular: "periodo", plural: "periodos" }}
            onPagina={setPaginaDeMetricas}
          />

          {/*
            ⭐ **«Qué significa cada métrica», DEBAJO DEL PAGINADO**
            (`PRD_R4.md:1686`, `R4-F3-CA-45`: *«debajo del paginado de la Tabla
            1»*). El orden en el DOM es el del PRD y el criterio lo lee en orden.

            Es un `<dl>` y no un `<ul>`: son pares rótulo/definición, que es
            exactamente lo que una lista de definiciones es. Un lector de
            pantalla las anuncia emparejadas.
          */}
          <section className="metricas__diccionario" aria-labelledby="metricas-diccionario">
            <h2 id="metricas-diccionario">{TITULO_DEL_DICCIONARIO}</h2>
            <dl>
              {diccionario.map((entrada) => (
                <div key={entrada.entrada} className="metricas__definicion-entrada">
                  <dt>{entrada.entrada}</dt>
                  <dd>{entrada.texto}</dd>
                </div>
              ))}
            </dl>
            {/*
              `R4-F3-CA-46` · la nota del brazo CON derivación, debajo del
              diccionario. `CA-47`: el brazo sin derivación no la tiene. Es el
              MISMO booleano que decide la columna y la entrada.
            */}
            {deriva && <p className="metricas__nota-del-brazo">{NOTA_DEL_BRAZO}</p>}
          </section>
        </div>
      )}

      {/* ── Pestaña «Errores de Meta» ─────────────────────────────────────── */}
      {!enResultados && (
        <div
          className="metricas__panel"
          role="tabpanel"
          id="metricas-panel-errores"
          aria-labelledby="metricas-pestana-errores"
        >
          {falloErrores && errores.items.length > 0 && (
            <p className="metricas__error" role="alert">
              {SIN_SERVIDOR}
            </p>
          )}

          <div className="tabla-rodante">
            <table className="metricas__tabla-errores">
              <caption className="metricas__caption">{leyendaDeErrores(sello)}</caption>
              {/*
                ⛔ **UN SOLO NIVEL, Y LA LISTA ES FIJA.** La Tabla 2 no agrupa
                (`PRD_R4.md:1716`: *«Columnas, en este orden: Item, Desde – Hasta
                y una columna por error de la lista fija»*) y **no tiene
                Acciones** (`:1726`: *«sin Acciones»*). Las seis columnas son las
                mismas en los dos brazos (`R4-F3-CA-44`): no hay filtro por
                capacidad acá.
              */}
              <thead>
                <tr>
                  <th scope="col">{COLUMNA_ITEM}</th>
                  <th scope="col">{COLUMNA_INTERVALO}</th>
                  {COLUMNAS_DE_ERROR.map((columna) => (
                    <th key={columna.rotulo} scope="col">
                      {columna.rotulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineaDeErrores !== null ? (
                  <tr>
                    <td colSpan={COLUMNAS_DE_ERROR.length + 2} className="metricas__vacio">
                      {lineaDeErrores}
                    </td>
                  </tr>
                ) : (
                  errores.items.map((fila: FilaDeErrores) => (
                    <tr key={fila.periodo.nombre}>
                      <td>{fila.periodo.nombre}</td>
                      <td>
                        <CeldaDelIntervalo desde={fila.periodo.desde} hasta={fila.periodo.hasta} />
                      </td>
                      {COLUMNAS_DE_ERROR.map((columna) => (
                        <td key={columna.rotulo} className="metricas__numero">
                          {/*
                            `PRD_R4.md:1726`: *«Las celdas muestran el número,
                            "0" cuando no hubo ninguno»*. El conteo y el reparto
                            de «Otros errores» los hace `conteoDeError`; acá no
                            se suma ni se filtra nada.
                          */}
                          {conteoDeError(fila.errors, columna)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Paginado
            etiqueta="errores de Meta"
            pagina={paginaDeErrores}
            total={errores.total}
            tamanoDePagina={tamanoDePagina}
            unidad={{ singular: "periodo", plural: "periodos" }}
            onPagina={setPaginaDeErrores}
          />

          {/* `R4-F3-CA-43` · la nota de «Otros errores», debajo de la Tabla 2. */}
          <p className="metricas__nota-de-errores">{NOTA_DE_LOS_ERRORES}</p>
        </div>
      )}

      {/* ── Los popups ────────────────────────────────────────────────────── */}
      {popupDePeriodo === "abierto" && (
        <PopupDePeriodo
          borde={borde}
          miga={sello}
          editando={editando}
          onCerrar={cerrarPopupDePeriodo}
          onGuardado={() => {
            cerrarPopupDePeriodo()
            refrescar()
          }}
        />
      )}

      {porBorrar !== null && (
        <PopupDeBorrado
          borde={borde}
          miga={sello}
          nombre={porBorrar}
          onCerrar={() => setPorBorrar(null)}
          onBorrado={() => {
            setPorBorrar(null)
            refrescar()
          }}
        />
      )}
    </section>
  )
}
